import React, { memo } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { statusCategoryForId } from '@jira/platform__status-categories/src/status-categories.tsx';
import Avatar from '@atlaskit/avatar';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { AvatarRelay } from '@atlassian/avatar-relay';
import { ScreenReaderText } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { shouldDisplayStrikethrough } from '@atlassian/jira-issue-key-with-resolution/src/common/utils.tsx';
import { isNonNullish } from '../../../../../../common/utils/index.tsx';
import type {
	footer_issueNavigator_FooterInternal$key,
	footer_issueNavigator_FooterInternal$data,
} from './__generated__/footer_issueNavigator_FooterInternal.graphql';
import messages from './messages.tsx';

type IssueKey = string;

type FieldSetEdges = footer_issueNavigator_FooterInternal$data['edges'];
type FieldSetEdge = NonNullable<FieldSetEdges>[number];

type FieldSetNode = NonNullable<FieldSetEdge>['node'];
type FieldSetNodeFields = NonNullable<FieldSetNode>['fields'];

type FieldEdges = NonNullable<FieldSetNodeFields>['edges'];
type FieldEdge = NonNullable<FieldEdges>[number];

/**
 * Find a field edge by fieldId and run it through the provided mapper function.
 */
export const byFieldId = <T,>(
	edges: FieldSetEdges,
	fieldId: string,
	mapperFn: (arg1: FieldEdge) => T,
): T | undefined =>
	edges
		?.map((edge: FieldSetEdge) => edge?.node?.fields?.edges?.[0])
		.filter((edge?: FieldEdge) => edge?.node?.fieldId === fieldId)
		.filter(isNonNullish)
		.map(mapperFn)[0];

export type Props = {
	issueKey: IssueKey;
	isResolved?: boolean | null;
	isSelected?: boolean;
	fieldSets: footer_issueNavigator_FooterInternal$key | null;
};

const FooterInternal = ({ issueKey, isResolved: isResolvedProp, isSelected, fieldSets }: Props) => {
	const { formatMessage } = useIntl();
	const fieldSetsData = useFragment<footer_issueNavigator_FooterInternal$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment footer_issueNavigator_FooterInternal on JiraIssueFieldSetConnection
			@argumentDefinitions(
				isJiraAiAgentAvatarSingleUserPickerNINEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-ai-agent-avatar-single-user-picker-nin.relayprovider"
				}
			) {
				edges {
					node {
						fields {
							edges {
								node {
									... on JiraSingleLineTextField {
										fieldId
										# eslint-disable-next-line @atlassian/relay/unused-fields -- used in the byFieldId util.
										text
									}
									... on JiraSingleSelectUserPickerField {
										fieldId
										name
										user {
											name
											picture
											...avatarRelay_UsePictureAndAppTypeFragment
												@include(if: $isJiraAiAgentAvatarSingleUserPickerNINEnabled)
										}
									}
									... on JiraIssueTypeField {
										fieldId
										issueType {
											name
											avatar {
												small
											}
										}
									}
									... on JiraStatusCategoryField {
										fieldId
										statusCategory {
											statusCategoryId
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		fieldSets,
	);
	const fieldSetEdges = fieldSetsData?.edges;

	const assignee = byFieldId(fieldSetEdges, 'assignee', (assigneeEdge) => assigneeEdge?.node?.user);

	const issueType = byFieldId(
		fieldSetEdges,
		'issuetype',
		(issueTypeEdge) => issueTypeEdge?.node?.issueType,
	);

	const statusCategoryId = byFieldId(
		fieldSetEdges,
		'statusCategory',
		(statusCategoryEdge) => statusCategoryEdge?.node?.statusCategory?.statusCategoryId,
	);

	const isResolved = shouldDisplayStrikethrough(
		isResolvedProp ?? undefined,
		statusCategoryForId(statusCategoryId),
	);

	return (
		<StyledContainer>
			<TypeIcon src={issueType?.avatar?.small ?? undefined} alt={issueType?.name ?? undefined} />
			<KeyText isSelected={isSelected} isResolved={isResolved}>
				{issueKey}
				{isResolved && (
					<ScreenReaderText role="status">
						{formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.screenReaderFooterKeyResolvedIssueTermRefresh
								: messages.screenReaderFooterKeyResolved,
							{ issueKey },
						)}
					</ScreenReaderText>
				)}
			</KeyText>
			<AvatarStyledContainer>
				<Tooltip content={assignee?.name}>
					{fg('jira_ai_agent_avatar_single_user_picker_nin') ? (
						<AvatarRelay fragmentRef={assignee ?? null} name={assignee?.name ?? ''} size="small" />
					) : (
						<Avatar src={assignee?.picture} name={assignee?.name} size="small" />
					)}
				</Tooltip>
			</AvatarStyledContainer>
		</StyledContainer>
	);
};

export const Footer = memo(FooterInternal);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TypeIcon = styled.img({
	width: '16px',
	height: '16px',
	marginRight: token('space.050'),
	borderRadius: token('radius.xsmall'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AvatarStyledContainer = styled.div({
	height: '28px',
	paddingLeft: token('space.100'),
	marginLeft: 'auto',
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const KeyText = styled.div<{
	isSelected: boolean | undefined;
	isResolved: boolean;
}>({
	font: token('font.body.UNSAFE_small'),
	// relative position required to prevent screen reader text from overflowing the card list scroll container
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	position: () => 'relative',
	fontWeight: token('font.weight.medium'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ isSelected }) =>
		isSelected ? token('color.text.selected') : token('color.text.subtle'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	textDecoration: ({ isResolved }) => (isResolved ? 'line-through' : 'none'),
});
